// frontend/src/modules/admin/components/GestionPeriodes.jsx - VERSION 2.0
import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit2, Trash2, Check, X, AlertCircle, Clock, Save, Zap } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GestionPeriodes = () => {
  const [periodes, setPeriodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showGeneratorModal, setShowGeneratorModal] = useState(false);
  const [editingPeriode, setEditingPeriode] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // État générateur
  const [generatorData, setGeneratorData] = useState({
    annee: new Date().getFullYear(),
    date_debut: '',
    date_fin: ''
  });

  // État du formulaire
  const [formData, setFormData] = useState({
    nom: '',
    date_debut_reservations: '',
    date_fin_reservations: '',
    date_livraison: '',
    statut: 'a_venir'
  });

  // Charger les périodes
  useEffect(() => {
    loadPeriodes();
  }, []);

  const loadPeriodes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/periodes`);
      const data = await response.json();
      
      if (data.success) {
        setPeriodes(data.data || []);
      }
    } catch (error) {
      console.error('Erreur chargement périodes:', error);
      showMessage('error', 'Erreur lors du chargement des périodes');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleOpenModal = (periode = null) => {
    if (periode) {
      setEditingPeriode(periode);
      setFormData({
        nom: periode.nom,
        date_debut_reservations: formatDateForInput(periode.date_debut_reservations),
        date_fin_reservations: formatDateForInput(periode.date_fin_reservations),
        date_livraison: formatDateForInput(periode.date_livraison),
        statut: periode.statut
      });
    } else {
      setEditingPeriode(null);
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      const endWeek = new Date(nextWeek);
      endWeek.setDate(endWeek.getDate() + 6);
      const delivery = new Date(endWeek);
      delivery.setDate(delivery.getDate() + 1);

      setFormData({
        nom: `Semaine ${getWeekNumber(nextWeek)}`,
        date_debut_reservations: formatDateForInput(nextWeek),
        date_fin_reservations: formatDateForInput(endWeek),
        date_livraison: formatDateForInput(delivery),
        statut: 'a_venir'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPeriode(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom || !formData.date_debut_reservations || 
        !formData.date_fin_reservations || !formData.date_livraison) {
      showMessage('error', 'Tous les champs sont obligatoires');
      return;
    }

    try {
      const url = editingPeriode 
        ? `${API_BASE}/periodes/${editingPeriode.id}`
        : `${API_BASE}/periodes`;
      
      const method = editingPeriode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', editingPeriode ? 'Période modifiée' : 'Période créée');
        handleCloseModal();
        loadPeriodes();
      } else {
        showMessage('error', data.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showMessage('error', 'Erreur lors de la sauvegarde');
    }
  };

  // 🆕 GÉNÉRATEUR AUTOMATIQUE DE PÉRIODES
  const handleGeneratePeriodes = async () => {
    const { annee, date_debut, date_fin } = generatorData;
    
    if (!annee && (!date_debut || !date_fin)) {
      showMessage('error', 'Choisir une année OU une plage de dates');
      return;
    }

    try {
      setLoading(true);
      
      let startDate, endDate;
      
      if (annee) {
        // Génération pour toute l'année
        startDate = new Date(annee, 0, 1); // 1er janvier
        endDate = new Date(annee, 11, 31); // 31 décembre
      } else {
        // Génération pour plage personnalisée
        startDate = new Date(date_debut);
        endDate = new Date(date_fin);
      }

      const periodesACreer = [];
      let currentDate = new Date(startDate);
      let weekNumber = getWeekNumber(currentDate);

      while (currentDate <= endDate) {
        const debutSemaine = new Date(currentDate);
        const finSemaine = new Date(currentDate);
        finSemaine.setDate(finSemaine.getDate() + 6); // Semaine de 7 jours
        const dateLivraison = new Date(finSemaine);
        dateLivraison.setDate(dateLivraison.getDate() + 1); // Livraison = lendemain

        // Déterminer le statut
        const now = new Date();
        let statut = 'a_venir';
        if (now >= debutSemaine && now <= finSemaine) {
          statut = 'en_cours';
        } else if (now > finSemaine) {
          statut = 'terminee';
        }

        periodesACreer.push({
          nom: `Semaine ${weekNumber}`,
          date_debut_reservations: formatDateForInput(debutSemaine),
          date_fin_reservations: formatDateForInput(finSemaine),
          date_livraison: formatDateForInput(dateLivraison),
          statut: statut
        });

        // Passer à la semaine suivante
        currentDate.setDate(currentDate.getDate() + 7);
        weekNumber = getWeekNumber(currentDate);
      }

      // Créer toutes les périodes (sans doublons)
      let created = 0;
      let skipped = 0;

      for (const periode of periodesACreer) {
        try {
          const response = await fetch(`${API_BASE}/periodes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(periode)
          });

          const data = await response.json();
          
          if (data.success) {
            created++;
          } else {
            skipped++;
          }
        } catch (error) {
          skipped++;
        }
      }

      showMessage('success', `${created} périodes créées, ${skipped} ignorées (doublons)`);
      setShowGeneratorModal(false);
      loadPeriodes();

    } catch (error) {
      console.error('Erreur génération:', error);
      showMessage('error', 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatut = async (periode, nouveauStatut) => {
    try {
      const response = await fetch(`${API_BASE}/periodes/${periode.id}/statut`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: nouveauStatut })
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', `Période ${nouveauStatut === 'en_cours' ? 'activée' : 'modifiée'}`);
        loadPeriodes();
      } else {
        showMessage('error', data.message || 'Erreur lors du changement de statut');
      }
    } catch (error) {
      console.error('Erreur changement statut:', error);
      showMessage('error', 'Erreur lors du changement de statut');
    }
  };

  const handleDelete = async (periode) => {
    if (!window.confirm(`Supprimer la période "${periode.nom}" ?\n\nATTENTION : Cette action est irréversible.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/periodes/${periode.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', 'Période supprimée');
        loadPeriodes();
      } else {
        showMessage('error', data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      showMessage('error', 'Erreur lors de la suppression');
    }
  };

  // Fonctions utilitaires
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const getStatutBadge = (periode) => {
    const now = new Date();
    const debut = new Date(periode.date_debut_reservations);
    const fin = new Date(periode.date_fin_reservations);

    // Période ACTIVE (en cours ET dans les dates)
    if (periode.statut === 'en_cours' && now >= debut && now <= fin) {
      return (
        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center gap-1">
          <Check className="h-4 w-4" />
          Active
        </span>
      );
    }

    // À venir
    if (periode.statut === 'a_venir' || (periode.statut === 'en_cours' && now < debut)) {
      return (
        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium flex items-center gap-1">
          <Clock className="h-4 w-4" />
          À venir
        </span>
      );
    }

    // Terminée
    if (periode.statut === 'terminee' || (periode.statut === 'en_cours' && now > fin)) {
      return (
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          Terminée
        </span>
      );
    }

    // Fermée
    if (periode.statut === 'fermee') {
      return (
        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
          Fermée
        </span>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Périodes</h2>
          <p className="text-gray-600 mt-1">Créez et gérez les périodes de réservation</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowGeneratorModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Zap className="h-5 w-5" />
            Générer périodes
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Nouvelle période
          </button>
        </div>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {message.text}
          </div>
        </div>
      )}

      {/* Liste des périodes */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {periodes.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-600 mb-2">Aucune période</h3>
            <p className="text-gray-500 mb-6">Créez votre première période de réservation</p>
            <button
              onClick={() => handleOpenModal()}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 inline-flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Créer une période
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Réservations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Livraison
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {periodes.map(periode => (
                  <tr key={periode.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{periode.nom}</div>
                      <div className="text-sm text-gray-500">ID: {periode.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        Du {formatDateForDisplay(periode.date_debut_reservations)}
                      </div>
                      <div className="text-sm text-gray-900">
                        au {formatDateForDisplay(periode.date_fin_reservations)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatDateForDisplay(periode.date_livraison)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatutBadge(periode)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(periode)}
                          className="text-blue-600 hover:text-blue-800 p-2"
                          title="Modifier"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>

                        {periode.statut === 'fermee' && (
                          <button
                            onClick={() => handleChangeStatut(periode, 'a_venir')}
                            className="text-green-600 hover:text-green-800 p-2"
                            title="Réouvrir"
                          >
                            <Check className="h-5 w-5" />
                          </button>
                        )}

                        {periode.statut !== 'fermee' && (
                          <button
                            onClick={() => handleChangeStatut(periode, 'fermee')}
                            className="text-orange-600 hover:text-orange-800 p-2"
                            title="Fermer"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        )}

                        <button
                          onClick={() => handleDelete(periode)}
                          className="text-red-600 hover:text-red-800 p-2"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Générateur */}
      {showGeneratorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Générer des périodes automatiquement
              </h3>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Option 1 : Générer toute une année
                  </label>
                  <select
                    value={generatorData.annee}
                    onChange={(e) => setGeneratorData({ ...generatorData, annee: parseInt(e.target.value), date_debut: '', date_fin: '' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Choisir une année --</option>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div className="text-center text-gray-500 text-sm">- OU -</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Option 2 : Plage de dates personnalisée
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Du</label>
                      <input
                        type="date"
                        value={generatorData.date_debut}
                        onChange={(e) => setGeneratorData({ ...generatorData, date_debut: e.target.value, annee: '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Au</label>
                      <input
                        type="date"
                        value={generatorData.date_fin}
                        onChange={(e) => setGeneratorData({ ...generatorData, date_fin: e.target.value, annee: '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Fonctionnement :</p>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>Crée des périodes de 7 jours (lundi→dimanche)</li>
                        <li>Livraison = lendemain de la fin</li>
                        <li>Les doublons sont automatiquement ignorés</li>
                        <li>Statuts définis automatiquement (passé/actif/futur)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-6 border-t mt-6">
                <button
                  onClick={() => setShowGeneratorModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleGeneratePeriodes}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Zap className="h-5 w-5" />
                  Générer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Création/Modification (inchangé) */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {editingPeriode ? 'Modifier la période' : 'Nouvelle période'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la période *
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: Semaine 45"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Début réservations *
                    </label>
                    <input
                      type="date"
                      value={formData.date_debut_reservations}
                      onChange={(e) => setFormData({ ...formData, date_debut_reservations: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fin réservations *
                    </label>
                    <input
                      type="date"
                      value={formData.date_fin_reservations}
                      onChange={(e) => setFormData({ ...formData, date_fin_reservations: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de livraison/retrait *
                  </label>
                  <input
                    type="date"
                    value={formData.date_livraison}
                    onChange={(e) => setFormData({ ...formData, date_livraison: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={formData.statut}
                    onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="a_venir">À venir</option>
                    <option value="en_cours">En cours</option>
                    <option value="fermee">Fermée</option>
                    <option value="terminee">Terminée</option>
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Statuts :</p>
                      <ul className="list-disc ml-5 space-y-1">
                        <li><strong>À venir</strong> : Période future</li>
                        <li><strong>En cours</strong> : Période active (UNE seule à la fois)</li>
                        <li><strong>Terminée</strong> : Période passée</li>
                        <li><strong>Fermée</strong> : Fermée manuellement</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <Save className="h-5 w-5" />
                    {editingPeriode ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionPeriodes;